import { ArrowDown, ArrowUp, Check, Pencil, Plus, Trash2, X } from "lucide-react";
import { useState } from "react";
import type { MomentCategoryKind, MomentCategoryNode } from "../../types";
import { MomentIcon } from "../../lib/momentIcon";

type Props = {
  categories: MomentCategoryNode[];
  busy: boolean;
  onClose: () => void;
  onCreate: (input: { parentId: string; kind: MomentCategoryKind; name: string }) => void;
  onRename: (id: string, name: string) => void;
  onDelete: (id: string) => void;
  onReorder: (items: Array<{ id: string; sortOrder: number; parentId: string | null }>) => void;
};

// Bottom sheet (mobile) / centered modal (desktop) to add / rename / delete / reorder categories.
// Reorder uses up/down buttons (touch-friendly; DnD deferred).
export function CategoryManageSheet({ categories, busy, onClose, onCreate, onRename, onDelete, onReorder }: Props) {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editValue, setEditValue] = useState("");
  const [newChild, setNewChild] = useState<Record<string, string>>({});

  const startEdit = (id: string, current: string) => {
    setEditingId(id);
    setEditValue(current);
  };
  const commitEdit = () => {
    if (editingId && editValue.trim()) {
      onRename(editingId, editValue.trim());
    }
    setEditingId(null);
    setEditValue("");
  };

  const move = (siblings: MomentCategoryNode[], index: number, dir: -1 | 1) => {
    const next = [...siblings];
    const target = index + dir;
    if (target < 0 || target >= next.length) {
      return;
    }
    [next[index], next[target]] = [next[target], next[index]];
    onReorder(next.map((node, i) => ({ id: node.id, sortOrder: i, parentId: node.parentId })));
  };

  const addChild = (root: MomentCategoryNode) => {
    const name = (newChild[root.id] ?? "").trim();
    if (!name) {
      return;
    }
    onCreate({ parentId: root.id, kind: root.kind, name });
    setNewChild((prev) => ({ ...prev, [root.id]: "" }));
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-end justify-center bg-slate-900/40 backdrop-blur-[1px] sm:items-center">
      <div className="flex max-h-[85dvh] w-full max-w-lg flex-col overflow-hidden rounded-t-2xl bg-white shadow-panel sm:rounded-2xl">
        <div className="flex items-center justify-between border-b border-line px-4 py-3">
          <p className="text-sm font-bold text-ink">Quản lý nhóm</p>
          <button
            type="button"
            aria-label="Đóng"
            onClick={onClose}
            className="flex h-9 w-9 items-center justify-center rounded-lg text-slate-500 hover:bg-slate-50"
          >
            <X size={18} aria-hidden="true" />
          </button>
        </div>

        <div className="min-h-0 flex-1 space-y-5 overflow-y-auto px-4 py-4">
          {categories.map((root) => (
            <section key={root.id}>
              <div className="mb-2 flex items-center gap-2 text-sm font-bold text-ink">
                <MomentIcon name={root.icon} size={16} className="text-muted" />
                <span>{root.name}</span>
              </div>

              <ul className="space-y-1.5">
                {root.children.map((child, index) => (
                  <li
                    key={child.id}
                    className="flex items-center gap-2 rounded-lg border border-line bg-slate-50 px-2.5 py-1.5"
                  >
                    <MomentIcon name={child.icon} size={15} className="shrink-0 text-muted" />
                    {editingId === child.id ? (
                      <input
                        autoFocus
                        value={editValue}
                        onChange={(event) => setEditValue(event.target.value)}
                        onKeyDown={(event) => {
                          if (event.key === "Enter") {
                            commitEdit();
                          }
                        }}
                        className="min-w-0 flex-1 rounded border border-line bg-white px-2 py-1 text-sm outline-none"
                      />
                    ) : (
                      <span className="min-w-0 flex-1 truncate text-sm text-ink">{child.name}</span>
                    )}

                    <div className="flex shrink-0 items-center gap-0.5 text-slate-400">
                      {editingId === child.id ? (
                        <button
                          type="button"
                          aria-label="Lưu tên"
                          onClick={commitEdit}
                          className="rounded p-1 hover:bg-white hover:text-brand"
                        >
                          <Check size={15} aria-hidden="true" />
                        </button>
                      ) : (
                        <button
                          type="button"
                          aria-label="Đổi tên"
                          onClick={() => startEdit(child.id, child.name)}
                          className="rounded p-1 hover:bg-white hover:text-brand"
                        >
                          <Pencil size={14} aria-hidden="true" />
                        </button>
                      )}
                      <button
                        type="button"
                        aria-label="Lên"
                        disabled={index === 0 || busy}
                        onClick={() => move(root.children, index, -1)}
                        className="rounded p-1 hover:bg-white hover:text-ink disabled:opacity-30"
                      >
                        <ArrowUp size={14} aria-hidden="true" />
                      </button>
                      <button
                        type="button"
                        aria-label="Xuống"
                        disabled={index === root.children.length - 1 || busy}
                        onClick={() => move(root.children, index, 1)}
                        className="rounded p-1 hover:bg-white hover:text-ink disabled:opacity-30"
                      >
                        <ArrowDown size={14} aria-hidden="true" />
                      </button>
                      {!child.isSystem ? (
                        <button
                          type="button"
                          aria-label="Xoá"
                          disabled={busy}
                          onClick={() => onDelete(child.id)}
                          className="rounded p-1 hover:bg-white hover:text-rose-600 disabled:opacity-30"
                        >
                          <Trash2 size={14} aria-hidden="true" />
                        </button>
                      ) : null}
                    </div>
                  </li>
                ))}
              </ul>

              <div className="mt-2 flex items-center gap-2">
                <input
                  value={newChild[root.id] ?? ""}
                  onChange={(event) => setNewChild((prev) => ({ ...prev, [root.id]: event.target.value }))}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      addChild(root);
                    }
                  }}
                  placeholder={`Thêm mục vào "${root.name}"`}
                  className="min-w-0 flex-1 rounded-lg border border-line bg-white px-3 py-1.5 text-sm outline-none focus:border-blue-300"
                />
                <button
                  type="button"
                  disabled={busy || !(newChild[root.id] ?? "").trim()}
                  onClick={() => addChild(root)}
                  className="inline-flex shrink-0 items-center gap-1 rounded-lg border border-line bg-white px-3 py-1.5 text-sm font-semibold text-ink hover:bg-slate-50 disabled:opacity-40"
                >
                  <Plus size={15} aria-hidden="true" />
                  Thêm
                </button>
              </div>
            </section>
          ))}
        </div>
      </div>
    </div>
  );
}
